/**
 * components/quotes/LinearDimension.tsx — Quota lineare (MUTA, PLAN quote v1).
 *
 * Riceve una `LinearQuote` GIA' risolta (due punti mondo + offset) e disegna:
 * linee di estensione (witness) dai punti misurati fino alla linea di quota,
 * la linea di quota stessa, due frecce di terminazione e l'etichetta al
 * centro. Nessun calcolo di geometria del pezzo — solo algebra di
 * posizionamento delle primitive di disegno (SPEC §6, AC-7: components/ MUTO).*/

import { Line } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { QUOTE_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";
import type { LinearQuote } from "../../model/quotes.types";
import { Arrowhead } from "./Arrowhead";
import { QuoteLabel } from "./QuoteLabel";

export interface LinearDimensionProps {
  quote: LinearQuote;
}

const FALLBACK_OFFSET_DIR: Vec3 = [0, 1, 0];

/** Render della quota lineare dal `LinearQuote` risolto.*/
export function LinearDimension({ quote }: LinearDimensionProps) {
  const geometry = useMemo(() => buildLinearGeometry(quote), [quote]);
  if (!geometry) return null;

  return (
    <group>
      <Line points={geometry.extensionA} color={COLORS.quoteExtensionLine} />
      <Line points={geometry.extensionB} color={COLORS.quoteExtensionLine} />
      <Line points={geometry.dimensionLine} color={COLORS.quoteDimensionLine} />
      <Arrowhead tip={geometry.arrowATip} direction={geometry.arrowADir} color={COLORS.quoteArrow} />
      <Arrowhead tip={geometry.arrowBTip} direction={geometry.arrowBDir} color={COLORS.quoteArrow} />
      <QuoteLabel
        position={geometry.labelPos}
        text={quote.label}
        awayFrom={geometry.labelAwayFrom}
      />
    </group>
  );
}

//─── Derivazione pura della geometria di disegno ─────────────────

interface LinearDimensionGeometry {
  extensionA: [THREE.Vector3, THREE.Vector3];
  extensionB: [THREE.Vector3, THREE.Vector3];
  dimensionLine: [THREE.Vector3, THREE.Vector3];
  arrowATip: Vec3;
  arrowADir: Vec3;
  arrowBTip: Vec3;
  arrowBDir: Vec3;
  labelPos: Vec3;
  /** Verso (`offsetDir`) in cui l'etichetta sta accanto alla linea — vedi `QuoteLabel.awayFrom`.*/
  labelAwayFrom: Vec3;
}

/**
 * Calcola le primitive di disegno dai due punti misurati + offset.
 * Ritorna `null` per segmenti degeneri (from ≈ to): non c'e' nulla da
 * disegnare, evita frecce/etichette sovrapposte a un punto.*/
function buildLinearGeometry(quote: LinearQuote): LinearDimensionGeometry | null {
  const from = new THREE.Vector3(...quote.from);
  const to = new THREE.Vector3(...quote.to);
  if (from.distanceToSquared(to) < 1e-9) return null;

  const offsetDir = new THREE.Vector3(...(quote.offsetDir ?? FALLBACK_OFFSET_DIR));
  if (offsetDir.lengthSq() < 1e-12) offsetDir.set(...FALLBACK_OFFSET_DIR);
  offsetDir.normalize();

  const offsetMm = quote.offsetMm ?? QUOTE_DEFAULTS.offsetMm;
  const overshoot = QUOTE_DEFAULTS.extensionOvershootMm;

  const dimFrom = from.clone().addScaledVector(offsetDir, offsetMm);
  const dimTo = to.clone().addScaledVector(offsetDir, offsetMm);
  const extFromEnd = from.clone().addScaledVector(offsetDir, offsetMm + overshoot);
  const extToEnd = to.clone().addScaledVector(offsetDir, offsetMm + overshoot);

  //Direzione dimFrom→dimTo: le frecce puntano VERSO l'esterno (tocco sulle
  //linee di estensione, code verso il centro — convenzione disegno tecnico).
  const dimDir = dimTo.clone().sub(dimFrom).normalize();
  //Etichetta a meta' linea, un gap verso l'esterno: `QuoteLabel` la mette
  //poi tutta da quel lato sullo schermo (30-09-2026 — prima stava sulle frecce).
  //`labelPlacement: "end"`: oltre la freccia finale, lungo la linea (quote corte).
  const atEnd = quote.labelPlacement === "end";
  const midpoint = atEnd
    ? dimTo.clone().addScaledVector(dimDir, QUOTE_DEFAULTS.labelGapMm)
    : dimFrom.clone().add(dimTo).multiplyScalar(0.5).addScaledVector(offsetDir, QUOTE_DEFAULTS.labelGapMm);
  const awayFrom = atEnd ? dimDir : offsetDir;

  return {
    extensionA: [from, extFromEnd],
    extensionB: [to, extToEnd],
    dimensionLine: [dimFrom, dimTo],
    arrowATip: [dimFrom.x, dimFrom.y, dimFrom.z],
    arrowADir: [-dimDir.x, -dimDir.y, -dimDir.z],
    arrowBTip: [dimTo.x, dimTo.y, dimTo.z],
    arrowBDir: [dimDir.x, dimDir.y, dimDir.z],
    labelPos: [midpoint.x, midpoint.y, midpoint.z],
    labelAwayFrom: [awayFrom.x, awayFrom.y, awayFrom.z],
  };
}
